import { useAuth0 } from "@auth0/auth0-react";
import { useMemo } from "react";
import type * as React from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ArrowTargetBecomeMemberIcon from "assets/images/arrow-target-become-member.svg";
import GLLogo from "assets/images/GL-Logo.svg";
import GiftMembershipStepPencilIcon from "assets/images/gift-membership-step-pencil.svg";
import LogoIconDefault from "assets/images/LogoIconDefault.svg";
import UnpaidInviteAccentLine from "assets/images/unpaid-invite-welcome-accent-line.svg";
import UnpaidInviteEmailExample from "assets/images/unpaid-invite-welcome-email-example.png";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import GLButton from "components/v3/sharedComponents/GLButton";
import HorizontalHowItWorksSteps, {
  horizontalHowItWorksImgIcon
} from "components/v3/sharedComponents/HorizontalHowItWorksSteps";
import { COLORS } from "components/v3/Theme/colors";
import PageTitle from "utils/PageTitle";
import GenomicLifeLegalDisclaimer from "components/v3/sharedComponents/GenomicLifeLegalDisclaimer";

const NAVIGATION_MAILTO = "mailto:navigation@genomiclife.com";

const publicPolicyLinkSx = {
  color: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px"
} as const;

const primaryCtaSx = {
  maxWidth: { sm: "none", xs: "80%" },
  width: { sm: "auto", xs: "80%" }
} as const;

const PaidInviteWelcomePage: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const { t } = useTranslation();

  PageTitle("Welcome");

  const goRegistration = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup"
      }
    });
  };

  const howItWorksSteps = useMemo(
    () => [
      {
        stepKey: "access",
        title: "Access your account",
        body: "Get started by setting up your account and personalizing your profile. You will need your invite code to complete this step.",
        icon: horizontalHowItWorksImgIcon(GiftMembershipStepPencilIcon, 24)
      },
      {
        stepKey: "health",
        title: "Answer a few health questions",
        body: "We will ask a few questions about your health, lifestyle, and any health conditions that may run in your family.",
        icon: horizontalHowItWorksImgIcon(ArrowTargetBecomeMemberIcon, 24)
      },
      {
        stepKey: "next",
        title: "Get personalized next steps",
        body: "This could mean requesting DNA screening, talking to our navigation team, or having a conversation with your doctor.",
        icon: horizontalHowItWorksImgIcon(LogoIconDefault, 24)
      }
    ],
    []
  );

  return (
    <GradientFramedLayout
      makeLogoClickable={false}
      showFooter
      showHeader={false}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: { md: 10, xs: 8 },
          marginX: "auto",
          maxWidth: 1280,
          minWidth: 0,
          pb: { md: 6, xs: 4 },
          pt: { md: 10, xs: 6 },
          px: { md: 4, xs: 2 },
          width: "100%"
        }}
      >
        <Box
          component="section"
          sx={{
            alignItems: "center",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            marginX: "auto",
            maxWidth: { md: 1140, xs: "100%" },
            textAlign: "center",
            width: "100%"
          }}
        >
          <Box
            alt={String(t("LOGO_ALT_TEXT"))}
            component="img"
            src={GLLogo}
            sx={{ display: "block", height: 44, width: 185 }}
          />
          <Box sx={{ position: "relative", width: "100%" }}>
            <Typography
              component="h1"
              sx={{
                display: "block",
                typography: { md: "h2_black", xs: "h3_black" },
                mt: 4,
                mb: 0
              }}
            >
              Unlock healthcare
            </Typography>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                mt: 0,
                position: "relative",
                width: "100%"
              }}
            >
              <Typography
                component="span"
                sx={{
                  background: `linear-gradient(to right, ${COLORS.ACCENT3}, ${COLORS.PRIMARY})`,
                  backgroundClip: "text",
                  display: "inline-block",
                  fontStyle: "italic",
                  typography: { md: "h2_serif_black", xs: "h3_serif_black" },
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  zIndex: 1
                }}
              >
                tailored to you.
              </Typography>
              <Box
                alt=""
                aria-hidden
                component="img"
                src={UnpaidInviteAccentLine}
                sx={{
                  display: "block",
                  height: { md: 25, xs: 10 },
                  marginTop: { md: -2.2, xs: -0.5 },
                  maxWidth: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  width: "100%",
                  zIndex: 0
                }}
              />
            </Box>
          </Box>
          <GLButton
            buttonType="primary"
            onClick={goRegistration}
            rightIcon={<ArrowForward />}
            text="Access your account"
            sx={primaryCtaSx}
          />
        </Box>

        <HorizontalHowItWorksSteps
          sectionSubtitle="It only takes a few minutes to get started."
          sectionTitle="Getting started is as easy as 1-2-3"
          steps={howItWorksSteps}
        />

        <Box
          component="section"
          sx={{
            alignItems: { lg: "flex-start", xs: "stretch" },
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: { lg: 8, md: 6, xs: 4 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: { lg: "1 1 0", xs: "none" },
              flexDirection: "column",
              gap: 3,
              minWidth: 0
            }}
          >
            <Typography component="h2" variant="h4_black">
              Looking for your invite code?
            </Typography>
            <Typography
              component="p"
              variant="body_dark_gray"
              sx={{ m: 0, mb: -2 }}
            >
              Check your inbox for an email from us. Don&apos;t see it? Check
              your spam folder. You can also{" "}
              <Box
                component="a"
                href={NAVIGATION_MAILTO}
                sx={{ ...publicPolicyLinkSx, fontWeight: 400 }}
              >
                contact our team
              </Box>{" "}
              for assistance.
            </Typography>
            <Typography
              component="p"
              sx={{ color: COLORS.DARK_GRAY, fontStyle: "italic", m: 0 }}
              variant="body_sm_dark_gray"
            >
              The email should have come from noreply@genomiclife-mail.com
            </Typography>
            <GLButton
              buttonType="primary"
              onClick={goRegistration}
              rightIcon={<ArrowForward />}
              text="Access your account"
              sx={{
                alignSelf: { lg: "flex-start", md: "center", xs: "stretch" },
                maxWidth: { lg: "100%", md: "60%", xs: "100%" },
                mt: { lg: 1, xs: 0 },
                width: { lg: "auto", md: "60%", xs: "100%" }
              }}
            />
          </Box>

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flex: { lg: "0 1 48%", xs: "none" },
              justifyContent: { lg: "flex-end", xs: "center" },
              minWidth: 0,
              width: { lg: "auto", xs: "100%" }
            }}
          >
            <Box
              alt="Example invitation email showing where to find your personal invite code"
              component="img"
              src={UnpaidInviteEmailExample}
              sx={{
                borderRadius: "16px",
                display: "block",
                height: "auto",
                maxWidth: { lg: "100%", xs: "min(100%, 400px)" },
                objectFit: "contain",
                width: { lg: "100%", xs: "auto" }
              }}
            />
          </Box>
        </Box>

        <Divider
          sx={{
            borderBottomWidth: "3px",
            borderColor: alpha(COLORS.PRIMARY_LIGHT, 0.2),
            my: { sm: -2, xs: 0 }
          }}
        />

        <GenomicLifeLegalDisclaimer
          component="footer"
          policyLinks="public"
          sx={{
            mx: "auto",
            pb: 2,
            textAlign: "left",
            width: "100%"
          }}
        />
      </Box>
    </GradientFramedLayout>
  );
};

export default PaidInviteWelcomePage;
